"use client";

import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import SourceCard from "@/app/(components)/generics/cards/SourceCard/SourceCard";
import IconLinkedIn from "@/app/(components)/generics/Icons/IconLinkedIn";
import CircleLoader from "@/app/(components)/generics/loaders/CircleLoader/CircleLoader";
import PointsLoader from "@/app/(components)/generics/loaders/PointsLoader/PointsLoader";
import InstallExtension from "@/app/(components)/specifics/extension/InstallExtension/InstallExtension";
import { useExtensionInstalled } from "@/app/hooks/useExtensionInstalled";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { ImportedBaseAvailability } from "@/app/types/imported-profile/ImportedBaseSearch";
import { ImportedProfileListTab } from "@/app/types/imported-profile/ImportedProfileListTab";
import { Limits } from "@/app/vars/Limits";
import ImportedBaseButton from "./ImportedBaseButton";
import ProspectSourceStat from "./ProspectSourceStat";
import styles from "./ProspectSources.module.css";

type Props = {
  lang: string;
  isPlaceholder?: boolean;
  isLoading?: boolean;
  count?: number;
  potentialCount?: number;
  foundCount?: number;
  rejectedCount?: number;
  importedBase?: ImportedBaseAvailability;
  importedBaseRunning?: boolean;
  onOpenImportedBase?: () => void;
  canAnalyze?: boolean;
  canRun?: boolean;
  isAnalyzing?: boolean;
  onAnalyze?: () => void;
  onViewImportedProfiles?: (tab?: ImportedProfileListTab) => void;
  onViewFound?: () => void;
};

export default function ProspectSourceLinkedin(props: Props) {
  const T = useT();
  const {
    lang,
    isPlaceholder = false,
    isLoading = false,
    count = 0,
    potentialCount = 0,
    foundCount = 0,
    rejectedCount = 0,
    importedBase,
    importedBaseRunning = false,
    onOpenImportedBase,
    canAnalyze = true,
    canRun = true,
    isAnalyzing = false,
    onAnalyze,
    onViewImportedProfiles,
    onViewFound,
  } = props;

  const isInstalled = useExtensionInstalled();

  const hasActivity = count > 0 || potentialCount > 0 || foundCount > 0 || rejectedCount > 0;
  const showStats = !isPlaceholder && hasActivity;

  const title = T(TKey.PROSPECT_SOURCE_LINKEDIN_TITLE);

  const renderActions = () => {
    if (isAnalyzing) {
      return (
        <span className={styles.searching}>
          <CircleLoader size={14} color="var(--color-green-3)" strokeWidth={2} />
          {T(TKey.PROSPECT_SOURCE_LINKEDIN_ANALYZING)}
        </span>
      );
    }

    if (!canRun) {
      return null;
    }

    if (isPlaceholder) {
      return (
        <Button
          isLink={false}
          type={ButtonType.PRIMARY}
          text={T(TKey.PROSPECT_SOURCE_LINKEDIN_EXTRACT_CTA)}
          onClick={onAnalyze}
          min={true}
        />
      );
    }

    if (!hasActivity) {
      if (isInstalled === true) {
        return (
          <Button
            isLink={false}
            type={ButtonType.PRIMARY}
            text={T(TKey.PROSPECT_SOURCE_LINKEDIN_EXTRACT_CTA)}
            onClick={onAnalyze}
            min={true}
            disabled={true}
          />
        );
      }
      return <InstallExtension lang={lang} text={T(TKey.INSTALL_EXTENSION) as string} />;
    }

    const hasWork = potentialCount > 0;

    return (
      <Button
        isLink={false}
        type={ButtonType.PRIMARY}
        text={T(TKey.PROSPECT_SOURCE_LINKEDIN_SEARCH_CTA)}
        onClick={onAnalyze}
        min={true}
        disabled={!canAnalyze || !hasWork}
      />
    );
  };

  const actions = renderActions();
  const showBenefits = !showStats && !isLoading;

  return (
    <SourceCard
      icon={<IconLinkedIn />}
      title={title as string}
      badge={
        isPlaceholder && showBenefits
          ? (T(TKey.PROSPECT_SOURCE_LINKEDIN_BADGE) as string)
          : undefined
      }
      headerAction={
        onOpenImportedBase ? (
          importedBase ? (
            <ImportedBaseButton
              data={importedBase}
              isRunning={importedBaseRunning}
              onOpen={onOpenImportedBase}
            />
          ) : (
            <PointsLoader dark={true} delay={Limits.PROSPECT_SEARCH_LOADER_DELAY} />
          )
        ) : undefined
      }
      description={showBenefits ? (T(TKey.PROSPECT_SOURCE_LINKEDIN_DESC) as string) : undefined}
      benefits={
        showBenefits
          ? [
              T(TKey.PROSPECT_SOURCE_LINKEDIN_BENEFIT_1) as string,
              T(TKey.PROSPECT_SOURCE_LINKEDIN_BENEFIT_2) as string,
              T(TKey.PROSPECT_SOURCE_LINKEDIN_BENEFIT_3) as string,
            ]
          : undefined
      }
      numberedBenefits={true}
      actions={actions}
      isLoading={isLoading}
    >
      {showStats && (
        <div className={styles.stats}>
          <ProspectSourceStat
            label={T(TKey.PROSPECT_STAT_IMPORTED) as string}
            value={count}
            onClick={
              count > 0
                ? () => onViewImportedProfiles?.(ImportedProfileListTab.IMPORTED)
                : undefined
            }
          />
          <ProspectSourceStat
            label={T(TKey.PROSPECT_STAT_POTENTIAL) as string}
            value={potentialCount}
            onClick={
              potentialCount > 0
                ? () => onViewImportedProfiles?.(ImportedProfileListTab.POTENTIAL)
                : undefined
            }
          />
          <ProspectSourceStat
            label={T(TKey.PROSPECT_STAT_FOUND) as string}
            value={foundCount}
            onClick={foundCount > 0 ? onViewFound : undefined}
          />
          <ProspectSourceStat
            label={T(TKey.PROSPECT_STAT_REJECTED) as string}
            value={rejectedCount}
            onClick={
              rejectedCount > 0
                ? () => onViewImportedProfiles?.(ImportedProfileListTab.REJECTED)
                : undefined
            }
          />
        </div>
      )}
    </SourceCard>
  );
}
